import { useCallback, useEffect, useState } from "react";
import { ApiError } from "./api";

// ---
// Reading and writing, once
//
// This was a `useLoad` private to `Admin.tsx` while nine other pages hand-rolled the same
// `useState`/`useEffect` pair, and ten repeated the same error-to-message line.
//
// The part that was missing everywhere is `pending`. Without it `data ?? []` renders the empty
// state while a request is in flight, so a tenant that is still loading says "No one holds a role
// in this tenant yet" — it tells you the answer is nothing when it does not yet know.
// ---

/**
 * One wording for a failure on an administrative page, where the code is actionable.
 *
 * Not for the pre-session forms: `bareMessage` is theirs, deliberately.
 */
export function messageOf(error: unknown): string {
  if (error instanceof ApiError) return `${error.code}: ${error.message}`;
  return error instanceof Error ? error.message : String(error);
}

/**
 * The server's own words, with no code attached — for sign-in, reset and invitation acceptance.
 *
 * The message for bad credentials is deliberately generic, and prefixing a code would both mean
 * nothing to the person reading it and risk turning the screen into an enumeration oracle.
 */
export function bareMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : String(error);
}

export interface Loaded<T> {
  /** What arrived, or null before it has. */
  readonly data: T | null;
  /** True until the first answer — success or failure — has arrived. */
  readonly pending: boolean;
  readonly error: string | null;
  readonly reload: () => void;
}

/** Loads on mount and whenever `deps` change; `reload()` asks again. */
export function useLoad<T>(load: () => Promise<T>, deps: unknown[]): Loaded<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(true);
  const [nonce, setNonce] = useState(0);
  const run = useCallback(load, deps);

  // biome-ignore lint/correctness/useExhaustiveDependencies: nonce is the refresh trigger — reload() increments it so this effect runs again
  useEffect(() => {
    let cancelled = false;
    setError(null);
    setPending(true);
    void run()
      .then((value) => {
        if (cancelled) return;
        setData(value);
        setPending(false);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setData(null);
        setError(messageOf(caught));
        setPending(false);
      });
    return () => {
      cancelled = true;
    };
  }, [run, nonce]);

  return { data, pending, error, reload: () => setNonce((n) => n + 1) };
}

export interface Action {
  /** Runs a mutation, then whatever should be reloaded. Failures become `error`. */
  readonly run: (mutate: () => Promise<unknown>) => void;
  readonly busy: boolean;
  readonly error: string | null;
  readonly clear: () => void;
}

/**
 * Runs a write and reports what happened.
 *
 * Every page had its own `act()` doing this, each catching and formatting in its own way, and
 * none of them disabling anything while the write was in flight — so a double click sent the
 * request twice.
 */
export function useAction(afterward?: () => void): Action {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return {
    busy,
    error,
    clear: () => setError(null),
    run: (mutate) => {
      setError(null);
      setBusy(true);
      void mutate()
        .then(() => {
          setBusy(false);
          afterward?.();
        })
        .catch((caught: unknown) => {
          setBusy(false);
          setError(messageOf(caught));
        });
    },
  };
}
